import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import Pagination from "react-js-pagination";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import OrgCell from "../../../components/OrgCell";
import PU from "../../../styled-components/popupBasicStyle";
import UTP from "../../../styled-components/userTablePopupStyle";

const EMAIL_REGEX = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/;

export default function SharePopUp({ open, close, project_id, ownerName }) {
  const [shareListInfo, setShareListInfo] = useState([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const postPerPage = 5;
  const suggestRef = useRef(null);

  const isEmail = EMAIL_REGEX.test(query.trim());
  const canAdd = query.trim().length > 0 && !loading;

  const fetchShareList = () => {
    if (!project_id) return;
    axios
      .get("/api/projects/shareProjectList?project_id=" + project_id)
      .then((response) => {
        setShareListInfo(Array.isArray(response.data.data) ? response.data.data : []);
      })
      .catch((error) => {
        console.error("Error SharePopUp shareList axios request:", error);
        setShareListInfo([]);
      });
  };

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setQuery("");
    setSuggestions([]);
    setShowSuggest(false);
    setMessage("");
    fetchShareList();
  }, [open, project_id]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const keyword = query.trim();
    if (!keyword) {
      setSuggestions([]);
      setShowSuggest(false);
      return;
    }

    const timer = setTimeout(() => {
      axios
        .get("/api/projects/searchShareMember?q=" + encodeURIComponent(keyword))
        .then((response) => {
          const sharedEmails = new Set(shareListInfo.map((item) => item.email));
          const list = (Array.isArray(response.data.data) ? response.data.data : []).map((item) => ({
            ...item,
            alreadyShared: sharedEmails.has(item.email),
          }));
          setSuggestions(list);
          setShowSuggest(true);
        })
        .catch(() => {
          setSuggestions([]);
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [query, open, shareListInfo]);

  if (!open) return null;

  const handleInnerClick = (e) => {
    e.stopPropagation();
  };

  const shareByEmail = (email) => {
    if (!email || loading) return;
    setLoading(true);
    setShowSuggest(false);
    axios
      .get("/api/projects/shareProject?email=" + encodeURIComponent(email) + "&project_id=" + project_id)
      .then((response) => {
        if (response.data.code === 10) {
          setMessage(response.data.message || "해당하는 회원이 없습니다.");
          return;
        }
        setMessage(response.data.msg || "공유에 성공했습니다.");
        setQuery("");
        setSuggestions([]);
        fetchShareList();
      })
      .catch((error) => {
        console.error("Error updateShareID axios request:", error);
        setMessage("공유에 실패했습니다.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const unshareMember = (item) => {
    const memberId = item.member_seq || item.member_id;
    if ((!memberId && !item.email) || loading) {
      setMessage("취소할 회원 정보가 없습니다.");
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({ project_id: String(project_id) });
    if (memberId) params.set("member_id", String(memberId));
    if (item.email) params.set("email", item.email);
    axios
      .get("/api/projects/unshareProject?" + params.toString())
      .then((response) => {
        setMessage(response.data.msg || "공유를 취소했습니다.");
        fetchShareList();
      })
      .catch((error) => {
        console.error("Error unshareProject axios request:", error);
        setMessage("공유 취소에 실패했습니다.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateShare = () => {
    const keyword = query.trim();
    if (!keyword || loading) return;

    if (isEmail) {
      shareByEmail(keyword);
      return;
    }

    const available = suggestions.filter((item) => !item.alreadyShared);
    if (available.length === 1) {
      shareByEmail(available[0].email);
      return;
    }

    if (suggestions.length > 0) {
      setMessage("검색 결과에서 회원을 선택해 주세요.");
      setShowSuggest(true);
      return;
    }

    setMessage("해당하는 회원이 없습니다.");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      updateShare();
    }
  };

  const currentList = shareListInfo.slice(
    page * postPerPage - postPerPage,
    page * postPerPage
  );

  return (
    <UTP.TotalDiv onClick={close}>
      <Panel onClick={handleInnerClick}>
        <PU.CloseDiv>
          <PU.CloseButton onClick={close}>
            <PU.Line />
            <PU.Line />
          </PU.CloseButton>
        </PU.CloseDiv>

        <UTP.PopupTitle>프로젝트 공유</UTP.PopupTitle>
        <SubHint>{ownerName ? `${ownerName} 님의 프로젝트` : "소유자의 프로젝트"}</SubHint>

        <AddRow ref={suggestRef}>
          <EmailInput
            type="text"
            placeholder="이름 또는 이메일로 검색"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setMessage("");
            }}
            onFocus={() => query.trim() && setShowSuggest(true)}
            onKeyDown={handleKeyDown}
          />
          <AddButton type="button" disabled={!canAdd} onClick={updateShare}>
            <PersonAddIcon fontSize="small" />
            추가
          </AddButton>
          {showSuggest && (
            <SuggestList>
              {suggestions.length > 0 ? (
                suggestions.map((item) => (
                  <SuggestItem
                    key={item.member_seq}
                    type="button"
                    disabled={item.alreadyShared}
                    onClick={() => !item.alreadyShared && shareByEmail(item.email)}
                  >
                    <strong>
                      {item.name}
                      {item.alreadyShared ? <SharedMark>공유됨</SharedMark> : null}
                    </strong>
                    <span>{item.email}</span>
                  </SuggestItem>
                ))
              ) : (
                <EmptySuggest>해당 단어가 들어간 회원이 없습니다.</EmptySuggest>
              )}
            </SuggestList>
          )}
        </AddRow>
        {message && <InfoText $error={message.includes("없") || message.includes("여러")}>{message}</InfoText>}

        <ListTitle>
          공유 회원 목록
          <Count>{shareListInfo.length}명</Count>
        </ListTitle>

        <UTP.TableWrapper>
          <CenterTable>
            <UTP.Thead>
              <tr>
                <UTP.Th>No</UTP.Th>
                <UTP.Th>이름</UTP.Th>
                <UTP.Th>이메일</UTP.Th>
                <UTP.Th>소속</UTP.Th>
                <UTP.Th>관리</UTP.Th>
              </tr>
            </UTP.Thead>
            <UTP.Tbody>
              {currentList.length > 0 ? (
                currentList.map((item, index) => (
                  <UTP.TbodyRow key={`${item.email}-${index}`}>
                    <UTP.Td>{(page - 1) * postPerPage + index + 1}</UTP.Td>
                    <UTP.Td>{item.name}</UTP.Td>
                    <UTP.Td>{item.email}</UTP.Td>
                    <UTP.Td>
                      <OrgCell name={item.org_name} type={item.org_type} />
                    </UTP.Td>
                    <UTP.Td>
                      <CancelBtn
                        type="button"
                        disabled={loading}
                        onClick={() => unshareMember(item)}
                      >
                        공유 취소
                      </CancelBtn>
                    </UTP.Td>
                  </UTP.TbodyRow>
                ))
              ) : (
                <UTP.TbodyRow>
                  <UTP.EmptyTd colSpan="5">공유된 회원이 없습니다.</UTP.EmptyTd>
                </UTP.TbodyRow>
              )}
            </UTP.Tbody>
          </CenterTable>
        </UTP.TableWrapper>

        {shareListInfo.length > postPerPage && (
          <Pager>
            <Pagination
              activePage={page}
              itemsCountPerPage={postPerPage}
              totalItemsCount={shareListInfo.length}
              pageRangeDisplayed={5}
              prevPageText={"<"}
              nextPageText={">"}
              onChange={setPage}
            />
          </Pager>
        )}
      </Panel>
    </UTP.TotalDiv>
  );
}

const Panel = styled(UTP.InnerTotalDiv)`
    width: min(820px, 92vw);
    height: auto;
    max-height: 84vh;
    padding: 28px 8px 20px;
`;

const SubHint = styled.p`
    margin: -8px 0 22px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
`;

const AddRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: calc(100% - 56px);
    margin-bottom: 8px;
    position: relative;
`;

const EmailInput = styled.input`
    flex: 1;
    height: 44px;
    padding: 0 16px;
    border: 1px solid #dbe3ee;
    border-radius: 12px;
    background: #f8fafc;
    font-size: 14px;
    color: #0f172a;
    outline: none;
    box-sizing: border-box;

    &:focus {
        border-color: #536dfe;
        background: #ffffff;
        box-shadow: 0 0 0 3px rgba(83, 109, 254, 0.12);
    }
`;

const SuggestList = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 106px;
    z-index: 5;
    max-height: 280px;
    overflow-y: auto;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
`;

const EmptySuggest = styled.div`
    padding: 14px;
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
    text-align: center;
`;

const SharedMark = styled.em`
    margin-left: 6px;
    font-style: normal;
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
`;

const SuggestItem = styled.button`
    appearance: none;
    font: inherit;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 100%;
    padding: 10px 14px;
    border: none;
    background: #ffffff;
    text-align: left;
    cursor: pointer;

    strong {
        font-size: 14px;
        color: #0f172a;
    }

    span {
        font-size: 12px;
        color: #64748b;
    }

    &:hover:not(:disabled) {
        background: #f5f8ff;
    }

    &:disabled {
        cursor: default;
        opacity: 0.65;
    }
`;

const AddButton = styled.button`
    appearance: none;
    font: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 44px;
    min-width: 96px;
    padding: 0 16px;
    border: none;
    border-radius: 12px;
    background: ${({ disabled }) => (disabled ? "#cbd5e1" : "#536dfe")};
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
    box-shadow: ${({ disabled }) =>
      disabled ? "none" : "0 8px 16px rgba(83, 109, 254, 0.28)"};
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover {
        background: ${({ disabled }) => (disabled ? "#cbd5e1" : "#3d5afe")};
        transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
    }
`;

const InfoText = styled.p`
    width: calc(100% - 56px);
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${({ $error }) => ($error ? "#e11d48" : "#2563eb")};
`;

const ListTitle = styled.h2`
    display: flex;
    align-items: center;
    gap: 8px;
    width: calc(100% - 56px);
    margin: 18px 0 12px;
    font-size: 16px;
    font-weight: 700;
    color: #111827;
`;

const Count = styled.span`
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 12px;
    font-weight: 700;
`;

const CenterTable = styled(UTP.StyledTable)`
    th,
    td {
        text-align: center !important;
    }
`;

const CancelBtn = styled.button`
    appearance: none;
    font: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid #fecaca;
    background: #fff1f2;
    color: #e11d48;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover:not(:disabled) {
        background: #ffe4e6;
        transform: translateY(-1px);
    }

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }
`;

const Pager = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 8px;
`;
